import { useRef, useState } from 'react';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';
import { useNavigate, useParams } from 'react-router-dom';

import { FormikProps } from 'formik';

import { Grid2 as Grid } from '@mui/material';

import { User } from '@/models/permission/AuthorityModel.ts';
import { Board } from '@/models/BoardModel.ts';
import { Card } from '@/models/card/CardModel.ts';

import { selectActiveBoard } from '../../ducks/Boards';
import { selectPossibleResponsible } from '../../ducks/Cards';
import { alertAction } from '../../thunks/Alerts';
import * as boardsActions from '../../thunks/Boards';
import * as cardsActions from '../../thunks/Cards';
import {
  getDefaultFieldValue,
  getInitialFieldValue,
  getNonMandatoryManualSequenceId,
  getRequiredFields,
  getUpdatedFieldsAndValues,
  hasResponsibleField,
  populateInitialValues,
  UNIQUE_DOC_ID,
} from '../../utils/FieldUtil';
import { validateAnsweredDateField } from '../../utils/Validators';
import { withViewModeQuery } from '../../utils/ViewModeUtil';

import CardAttachments from '../common/Attachments';
import CardComments from '../common/Comments';
import Form from '../common/Form';
import PageWrapper from '../../common/PageWrapper';
import CardStep from './CardStep';

const mapStateToProps = () =>
  createStructuredSelector({
    board: selectActiveBoard(),
    possibleResponsible: selectPossibleResponsible(),
  });

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators({ ...cardsActions, ...boardsActions }, dispatch),
});

const CardCreateContainer = ({
  board,
  possibleResponsible,
  actions,
}: {
  board: Board;
  possibleResponsible: User[];
  actions: any;
}) => {
  const navigate = useNavigate();
  const { communityId, roomId, boardId } = useParams();
  const formRef = useRef<FormikProps<any>>();
  const [closeOnSuccess, setCloseOnSuccess] = useState(false);

  const handleSubmit = ({ values, item }: { values: { [id: string]: any }; item: Card }) => {
    const data = {
      type: 'entry',
      fields: getUpdatedFieldsAndValues({ values, item, fieldsConfig: board.field_config.fields }),
    };

    return actions.createCard({ communityId, roomId, boardId, data }).then((action: any) => {
      const errorMessage =
        action.payload.error?.response?.status === 403
          ? intl.get('card.create.forbidden')
          : intl.get('card.create.error');
      alertAction({
        action,
        error: errorMessage,
        success: intl.get('card.create.success'),
        onSuccess: () =>
          navigate(
            withViewModeQuery(
              closeOnSuccess
                ? `/${communityId}/${roomId}/boards/${board.id}`
                : `/${communityId}/${roomId}/boards/${board.id}/cards/${action.payload.card.id}`,
            ),
          ),
      });
    });
  };

  const handleCancel = () => {
    navigate(withViewModeQuery(`/${communityId}/${roomId}/boards/${boardId}`));
  };

  const handleFetchPossibleResponsible = ({ disciplines }: { disciplines: string[] }) => {
    if (hasResponsibleField({ fieldConfig: board.field_config })) {
      actions
        .fetchPossibleResponsible({ communityId, roomId, boardId, disciplines })
        .then((action: Action) => alertAction({ action, error: intl.get('card.responsible.error') }));
    }
  };

  const handleSaveClick = (closeForm: boolean) => {
    const form = formRef.current;
    setCloseOnSuccess(closeForm);

    if (form) {
      form.handleSubmit();
    }
  };

  // Create new dummy card
  const card: Card = {
    type: 'entry',
    fields: board.field_config.fields
      .filter((field) => field.type !== 'auto-number')
      .map((field) => {
        const value = field.initial_value ? getInitialFieldValue(field) : getDefaultFieldValue(field);

        return {
          id: field.id,
          name: field.name,
          type: field.type,
          value: value,
        };
      }),
    permissions: {},
  };

  return (
    <PageWrapper title={intl.get('app_bar.question_card')}>
      <Grid container spacing={2}>
        <Grid size={{ sm: 12, md: 8, xs: 12 }}>
          <Form
            formRef={formRef}
            item={card}
            possibleResponsible={possibleResponsible}
            initialValues={populateInitialValues({
              item: card,
              fields: board.field_config.fields,
            })}
            disabledFields={[
              board.field_config.answer_field,
              board.field_config.answered_date_field,
              UNIQUE_DOC_ID,
              ...getNonMandatoryManualSequenceId(board.field_config.fields),
            ]}
            requiredFields={getRequiredFields({ item: card, fields: board.field_config.fields })}
            type={board.type}
            fieldConfig={board.field_config}
            sequenceNumberDigits={board.workflow_config?.sequence_number_digits}
            onFetchPossibleResponsible={handleFetchPossibleResponsible}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            restrictions={{
              'task-done': () => ({}),
              'task-responsible': () => ({}),
              'rich-text': () => ({}),
              'auto-number': () => ({}),
              'unique-document-id': () => ({}),
              'sequence-number': () => ({}),
              member: () => ({}),
              list: () => ({}),
              date: (fieldConfig, field) => {
                if (fieldConfig.answered_date_field === field.id) {
                  return { disableFuture: true };
                }
              },
              string: (fieldConfig, field) => {
                return (field.id === fieldConfig.question_field || field.id === fieldConfig.answer_field) &&
                  field.type === 'string'
                  ? { variant: 'outlined', multiline: true, minRows: 5, maxRows: 10 }
                  : {};
              },
              numeric: () => ({}),
            }}
            formValidation={{
              answered_date_field: (fieldConfig, values) => validateAnsweredDateField({ fieldConfig, values }),
            }}
            submitOptions={[
              {
                title: intl.get('common.form.save_and_close'),
                handleClick: () => handleSaveClick(true),
              },
              {
                title: intl.get('common.form.save'),
                default: true,
                handleClick: () => handleSaveClick(false),
              },
            ]}
          />
          <CardAttachments
            activeCommunity={communityId}
            activeRoom={roomId}
            item={card}
            attachments={[]}
            isUploading={false}
            isDeleting={false}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 4, xs: 12 }}>
          <CardStep card={card} steps={board.step_config.steps} />
          <CardComments board={board} item={card} comments={[]} members={[]} />
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

const ConnectedCardCreateContainer = connect(mapStateToProps, mapDispatchToProps)(CardCreateContainer);

export default ConnectedCardCreateContainer;
